import {action, computed, observable, makeObservable} from 'mobx';
import {observer} from 'mobx-react';
import PropTypes from 'prop-types';
import React, {Component} from 'react';
import {toJS} from 'mobx';

//lodash
import get from 'lodash/get';
import omit from 'lodash/omit';

//components
import Picture from 'components/Picture';

//helpers
import {convertFocusPointToCss, getImageAspectRatio, getImageSourceProps} from 'utils/image-utils';

//styles
import {ContainerWithConstantAspectRatio, ContainerWithVariableAspectRatio, StyledPicture} from './styles';

@observer
class CloudinaryPictureComponent extends Component {
  static defaultProps = {
    sizes: '400px'
  };

  static propTypes = {
    aspectRatio: PropTypes.number,
    metadata: PropTypes.object
  };

  @observable measuredContainerAspectRatio = null;
  @observable measuredImageAspectRatio = null;
  @observable measuredImageOriginalSrc = null;

  constructor(props) {
    super(props);
    makeObservable(this);
  }

  @computed
  get containerAspectRatio() {
    const {aspectRatio} = this.props;
    return aspectRatio || this.measuredContainerAspectRatio;
  }

  @computed
  get imageAspectRatio() {
    const {metadata} = this.props;
    const crop = get(metadata, 'crop');

    if (metadata && metadata.crop) {
      return getImageAspectRatio(crop, metadata.rotation);
    }

    return this.measuredImageAspectRatio;
  }

  @computed
  get objectPositionProps() {
    const {metadata} = this.props;

    if (!this.containerAspectRatio || !this.imageAspectRatio) {
      return null;
    }

    const {kx: objectPositionX, ky: objectPositionY} = convertFocusPointToCss(
      this.containerAspectRatio,
      this.imageAspectRatio,
      metadata
    );

    return {objectPositionX, objectPositionY};
  }

  get sourceProps() {
    const {metadata, src} = this.props;

    if (!this.containerAspectRatio) {
      return null;
    }

    return getImageSourceProps(this.containerAspectRatio, this.imageAspectRatio, src, omit(metadata, 'contain'));
  }

  @action
  setMeasuredImageAspectRatio = value => {
    this.measuredImageAspectRatio = value;
  };

  @action
  setMeasuredImageOriginalSrc = value => {
    this.measuredImageOriginalSrc = value;
  };

  @action
  setMeasuredContainerAspectRatio = value => {
    this.measuredContainerAspectRatio = value;
  };

  handlePictureLoad = event => {
    const {src} = this.props;
    const {naturalWidth, naturalHeight} = event.target;

    if (src !== this.measuredImageOriginalSrc) {
      this.setMeasuredImageAspectRatio(naturalWidth / naturalHeight);
      this.setMeasuredImageOriginalSrc(src);
    }
  };

  handleResize = event => {
    const {width, height} = event;
    this.setMeasuredContainerAspectRatio(width / height);
  };

  render() {
    const {aspectRatio} = this.props;

    const forwardedProps = omit(this.props, [
      ...Object.keys(CloudinaryPictureComponent.propTypes),
      ...Object.keys(Picture.propTypes)
    ]);

    if (aspectRatio) {
      return (
        <ContainerWithConstantAspectRatio {...forwardedProps}>
          {this.renderPicture({shouldBeAbsolutelyPositioned: true})}
          <div style={{paddingTop: `${100 / aspectRatio}%`}} />
        </ContainerWithConstantAspectRatio>
      );
    }

    return (
      <ContainerWithVariableAspectRatio onResize={this.handleResize} {...forwardedProps}>
        {this.renderPicture()}
      </ContainerWithVariableAspectRatio>
    );
  }

  renderPicture({shouldBeAbsolutelyPositioned = false} = {}) {
    const {
      emptyIcon,
      metadata,
      restartAnimation,
      shouldShowSpinnerWhileLoading,
      shouldShowAnimationWhileLoading,
      shouldShowOverlayShapes,
      sizes,
      loadingImageCard
    } = this.props;

    const objectFit = get(metadata, 'contain') ? 'contain' : 'cover';
    const backgroundColor = objectFit === 'contain' ? get(metadata, 'backgroundColor', '#FFF') : undefined;
    const overlayShapes = get(toJS(metadata), 'overlays');

    return (
      <StyledPicture
        backgroundColor={backgroundColor}
        emptyIcon={!loadingImageCard && emptyIcon}
        objectFit={objectFit}
        metadata={metadata}
        onLoad={this.handlePictureLoad}
        restartAnimation={restartAnimation}
        shouldBeAbsolutelyPositioned={shouldBeAbsolutelyPositioned}
        shouldShowSpinnerWhileLoading={shouldShowSpinnerWhileLoading}
        shouldShowAnimationWhileLoading={shouldShowAnimationWhileLoading}
        shouldShowOverlayShapes={overlayShapes && shouldShowOverlayShapes}
        overlayShapes={overlayShapes}
        sizes={sizes}
        {...this.objectPositionProps}
        {...this.sourceProps}
      />
    );
  }
}

export default CloudinaryPictureComponent;
